import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useV2GetLobApprovalQuery,
  useV2ListUsersForAccountQuery,
  useV2ListLineOfBusinessesForAccountQuery,
} from "@generated";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { AdminFormPage } from "@components/CatalogIndex";
import { LobApprovalForm } from "../LobApprovalForm/LobApprovalForm";
import { ApproversSection } from "../ApproversSection/ApproversSection";
import { useLobApprovalsList } from "../api/useLobApprovalsList";
import { useLobApprovalMutations } from "../api/useLobApprovalMutations";
import type { LobApprovalFormValues } from "../types/LobApproval";

const FORM_ID = "lob-approval-form";

export const Edit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, refetch } = useV2GetLobApprovalQuery({
    id: Number(id),
    include: ["section"],
  });
  const { rows: allApprovals, refetch: refetchApprovals } =
    useLobApprovalsList();
  const { data: sectionsData } = useV2ListLineOfBusinessesForAccountQuery({});
  const { data: usersData } = useV2ListUsersForAccountQuery({});
  const { update } = useLobApprovalMutations();
  const [submitting, setSubmitting] = useState(false);

  const current = allApprovals.find((a) => a.id === id);

  const initial = useMemo<LobApprovalFormValues | null>(() => {
    if (!data?.data) return null;
    const record: any = data.data;
    return {
      sectionId: String(record.relationships?.section?.data?.id ?? ""),
      requiredApprovals: record.attributes["required-approvals"] ?? 1,
      submitterMayApprove: !!record.attributes["submitter-may-approve"],
      limitApprovalsToTeam: !!record.attributes["limit-approvals-to-team"],
    };
  }, [data]);

  const availableSections = useMemo(() => {
    const allSections = (sectionsData?.data ?? []).map((s: any) => ({
      id: String(s.id),
      name: s.attributes?.name ?? "",
    }));
    const usedByOtherApprovals = new Set(
      allApprovals.filter((a) => a.id !== id).map((a) => a.sectionId)
    );
    return allSections.filter((s: any) => !usedByOtherApprovals.has(s.id));
  }, [sectionsData, allApprovals, id]);

  const availableUsers = useMemo(
    () =>
      (usersData?.data ?? []).map((u: any) => ({
        id: String(u.id),
        name: u.attributes?.name ?? u.attributes?.identifier ?? "",
      })),
    [usersData]
  );

  if (isLoading || !initial || !current) return <ScopeStackSpinner />;

  const recordName = current.name || "Approval";

  const handleSubmit = async (values: LobApprovalFormValues) => {
    setSubmitting(true);
    try {
      await update(id!, values);
      navigate("..");
    } catch (err) {
      console.error("Failed to update lob_approval", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdminFormPage
      title={`Edit ${recordName}`}
      formId={FORM_ID}
      onCancel={() => navigate("..")}
      isSubmitting={submitting}
    >
      <LobApprovalForm
        id={FORM_ID}
        initialValues={initial}
        availableSections={availableSections}
        isSubmitting={submitting}
        onSubmit={handleSubmit}
      />
      <ApproversSection
        lobApprovalId={id!}
        approvers={current.approvers}
        availableUsers={availableUsers}
        onChanged={() => {
          // ApproversSection rows come from the list (via current.approvers),
          // not from the GET-by-id call. Refresh the list so the table
          // reflects the just-saved/deleted row; also refresh the parent GET
          // so the relationship linkage stays in sync.
          refetchApprovals();
          refetch();
        }}
      />
    </AdminFormPage>
  );
};
